if (!customElements.get('nordelia-angle-controls')) {
  customElements.define('nordelia-angle-controls', class extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-nd-config]').textContent);
      this.gallery = this.closest('media-gallery');
      this.config.media = this.config.media.filter(m => m.media_type === 'image');
      const option = this.config.options.find(o => /^(farbe|colou?r|design)$/i.test(o.name));
      this.colorOption = option;
      this.normalizeColor = value => String(value || '')
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const values = option ? option.values.map(v => typeof v === 'string' ? v : v.name) : [];
      this.fronts = values.map(color => {
        const tagged = this.config.media.find(m => m.alt?.split(' – ')[1] === color && m.alt?.endsWith(' – Vorderansicht'));
        const colorToken = new RegExp('(?:^|-)' + this.normalizeColor(color) + '(?:-|$)');
        const frontPhoto = this.config.media.find(m => {
          const filename = (m.preview_image?.src || '').split('?')[0].split('/').pop() || '';
          const key = this.normalizeColor(filename);
          return colorToken.test(key) && /(?:^|-)(?:front|vorderansicht)(?:-|$)/.test(key);
        });
        const variant = this.config.variants.find(v => v.options[option.position - 1] === color && v.featured_media);
        const old = this.config.fronts.find(f => f.color === color);
        const named = this.config.media.find(m => m.alt?.split(' – ')[1] === color);
        const preferredPose = this.config.media.find(m => {
          const filename = (m.preview_image?.src || '').split('?')[0].split('/').pop() || '';
          const imageKey = this.normalizeColor(filename);
          const altKey = this.normalizeColor(m.alt || '');
          const colorMatches = colorToken.test(imageKey) || colorToken.test(altKey);
          const intentionallyMarked = /(?:^|-)(?:hero-angle|schuin-achter|schraeg-hinten|dreiviertel-hinten)(?:-|$)/.test(imageKey + '-' + altKey);
          return colorMatches && intentionallyMarked;
        });
        const id = [preferredPose?.id, tagged?.id, frontPhoto?.id, variant?.featured_media.id, old?.id, named?.id]
          .find(candidate => this.config.media.some(m => String(m.id) === String(candidate)));
        return { id: String(id || ''), color };
      });
      if (!values.length && this.config.media.length) this.fronts = [{ id: String(this.config.media[0].id), color: '' }];
      if (!this.gallery || !this.fronts.some(front => front.id)) return;
      this.order = ['Linke Seitenansicht', 'Rechte Seitenansicht', 'Rückansicht', 'Produktfoto ohne Model'];
      this.className = 'nd-angle-gallery';
      this.angles = document.createElement('div');
      this.angles.className = 'nd-angles';
      this.angles.setAttribute('role', 'group');
      this.angles.setAttribute('aria-label', this.config.galleryLabel);
      this.stage = document.createElement('button');
      this.stage.type = 'button';
      this.stage.className = 'nd-stage';
      this.stage.addEventListener('click', event => {
        this.setInlineZoom(!this.zoomed);
        if (this.zoomed && event.detail) this.moveInlineZoom(event);
      });
      this.stage.addEventListener('pointermove', event => {
        if (this.zoomed && event.pointerType === 'mouse') this.moveInlineZoom(event);
      });
      this.stage.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.setInlineZoom(false);
        if (!this.zoomed || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        this.zoomX = Math.max(0, Math.min(100, this.zoomX + (event.key === 'ArrowLeft' ? -10 : event.key === 'ArrowRight' ? 10 : 0)));
        this.zoomY = Math.max(0, Math.min(100, this.zoomY + (event.key === 'ArrowUp' ? -10 : event.key === 'ArrowDown' ? 10 : 0)));
        this.stage.style.setProperty('--nd-origin', this.zoomX + '% ' + this.zoomY + '%');
      });
      this.colors = document.createElement('div');
      this.colors.className = 'nd-colors';
      this.append(this.angles, this.stage, this.colors);
      this.fronts.forEach(front => {
        const media = this.config.media.find(m => String(m.id) === front.id);
        const button = this.photoButton(media, front.color);
        button.dataset.color = front.color;
        button.addEventListener('click', () => {
          this.selectColor(front);
          const root = this.closest('product-info') || document;
          const radio = [...root.querySelectorAll('variant-selects input[type="radio"]')].find(r => r.value === front.color);
          if (radio && !radio.checked) radio.click();
          else if (!radio) {
            const select = [...root.querySelectorAll('variant-selects select')].find(s => [...s.options].some(o => o.value === front.color));
            if (select && select.value !== front.color) {
              select.value = front.color;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
        this.colors.append(button);
      });
      this.productRoot = this.closest('product-info') || this.closest('.shopify-section') || this.parentElement;
      this.onColorChange = event => {
        const input = event.target;
        if (!input.closest('variant-selects')) return;
        const front = this.selectedColorFront();
        if (front) this.selectColor(front);
      };
      this.productRoot.addEventListener('change', this.onColorChange);
      this.selectColor(this.selectedColorFront() || this.fronts.find(f => f.id === String(this.config.selected)) || this.fronts[0]);
      await customElements.whenDefined('media-gallery');
      const original = this.gallery.setActiveMedia.bind(this.gallery);
      this.gallery.setActiveMedia = (id, prepend) => {
        const front = this.selectedColorFront() || this.fronts.find(f => f.id && String(id).endsWith('-' + f.id));
        if (front) this.selectColor(front);
        else original(id, prepend);
      };
      this.gallery.classList.add('nd-has-angles');
    }
    selectedColorFront() {
      const picker = this.productRoot?.querySelector('variant-selects');
      if (!picker || !this.colorOption) return null;
      const groups = [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')];
      const group = groups[this.colorOption.position - 1];
      const input = group?.querySelector('input[type="radio"]:checked, select');
      return this.fronts.find(front => front.color === input?.value) || null;
    }
    disconnectedCallback() {
      this.productRoot?.removeEventListener('change', this.onColorChange);
    }
    setInlineZoom(enabled) {
      enabled = Boolean(enabled && this.activeImage);
      this.zoomed = enabled;
      this.zoomX = this.zoomY = 50;
      this.stage.style.setProperty('--nd-origin', '50% 50%');
      this.stage.classList.toggle('nd-is-zoomed', enabled);
      this.stage.setAttribute('aria-pressed', String(enabled));
      this.stage.setAttribute('aria-label', this.activeImage
        ? (enabled ? this.config.zoomOutLabel : this.config.zoomInLabel)
        : this.config.imageUnavailableLabel);
      if (enabled && this.activeImage) {
        const url = new URL(this.activeMedia.preview_image.src, location.href);
        url.searchParams.set('width', '3000');
        this.activeImage.src = url.href;
      }
    }
    moveInlineZoom(event) {
      const rect = this.stage.getBoundingClientRect();
      this.zoomX = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100));
      this.zoomY = Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100));
      this.stage.style.setProperty('--nd-origin', this.zoomX + '% ' + this.zoomY + '%');
    }
    image(media, width) {
      const img = document.createElement('img');
      const url = new URL(media.preview_image.src, location.href);
      url.searchParams.set('width', width);
      img.src = url.href;
      img.draggable = false;
      img.alt = media.alt || '';
      img.width = media.preview_image.width;
      img.height = media.preview_image.height;
      return img;
    }
    photoButton(media, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.title = label;
      if (media) button.append(this.image(media, 160));
      else {
        button.classList.add('nd-color-without-image');
        button.textContent = label;
      }
      return button;
    }
    selectColor(front) {
      if (this.color === front.color) return;
      this.color = front.color;
      const media = this.config.media.filter(m => String(m.id) === front.id);
      this.order.forEach(angle => {
        const match = this.config.media.find(m => m.alt?.split(' – ')[1] === front.color && m.alt?.endsWith(' – ' + angle));
        if (match && !media.some(m => String(m.id) === String(match.id))) media.push(match);
      });
      const frontIds = new Set(this.fronts.map(f => f.id));
      const colorToken = new RegExp('(?:^|-)' + this.normalizeColor(front.color) + '(?:-|$)');
      this.config.media.forEach(m => {
        if (media.includes(m) || frontIds.has(String(m.id))) return;
        const parts = (m.alt || '').split(' – ');
        const filename = (m.preview_image?.src || '').split('?')[0].split('/').pop() || '';
        const matchesColor = parts[1] === front.color
          || colorToken.test(this.normalizeColor(filename))
          || colorToken.test(this.normalizeColor(m.alt));
        if (matchesColor || this.fronts.length === 1) media.push(m);
      });
      const anglePriority = item => {
        if (String(item.id) === front.id) return 0;
        const key = this.normalizeColor((item.preview_image?.src || '') + ' ' + (item.alt || ''));
        if (/(?:^|-)(?:left|links|linke|seite-0)(?:-|$)/.test(key)) return 1;
        if (/(?:^|-)(?:right|rechts|rechte|seite-1)(?:-|$)/.test(key)) return 2;
        if (/(?:^|-)(?:back|rueckansicht|rueck)(?:-|$)/.test(key)) return 3;
        if (/(?:^|-)(?:produkt|produktfoto|produktansicht|product|flat)(?:-|$)/.test(key)) return 4;
        return 5;
      };
      media.sort((a, b) => anglePriority(a) - anglePriority(b));
      this.angleMedia = media;
      this.angles.replaceChildren();
      this.stage.replaceChildren();
      this.activeImage = this.activeMedia = null;
      this.stage.disabled = media.length === 0;
      this.stage.classList.toggle('nd-image-unavailable', !media.length);
      this.setInlineZoom(false);
      if (!media.length) {
        const message = document.createElement('span');
        message.textContent = this.config.imageUnavailableLabel;
        this.stage.append(message);
      }
      media.forEach((m, i) => {
        const img = this.image(m, 1400);
        img.hidden = i !== 0;
        this.stage.append(img);
        const button = this.photoButton(m, m.alt || front.color);
        const select = () => {
          [...this.stage.children].forEach(el => { el.hidden = el !== img; });
          [...this.angles.children].forEach(el => el.setAttribute('aria-current', String(el === button)));
          this.activeImage = img;
          this.activeMedia = m;
          this.setInlineZoom(false);
        };
        button.addEventListener('pointerenter', select);
        button.addEventListener('focus', select);
        button.addEventListener('click', select);
        this.angles.append(button);
        if (i === 0) select();
      });
      [...this.colors.children].forEach(b => b.setAttribute('aria-current', String(b.dataset.color === front.color)));
    }
  });
}



